import { v4 as uuidv4 } from "uuid";

/* -----------------------------------------------------------
   SAFE JSON PARSER
----------------------------------------------------------- */
const safeJsonParse = <T>(str: string | null, fallback: T): T => {
  if (!str) return fallback;
  try {
    return JSON.parse(decodeURIComponent(str));
  } catch {
    return fallback;
  }
};

/* -----------------------------------------------------------
   COOKIE HELPERS
----------------------------------------------------------- */
export const getCookieRaw = (name: string): string | null => {
  if (typeof document === "undefined") return null;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = document.cookie.match(new RegExp("(^| )" + escaped + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
};

export const setCookie = (name: string, value: unknown, days = 7): void => {
  const expires = new Date(Date.now() + days * 86400000).toUTCString();
  const secure = window.location.protocol === "https:" ? ";Secure" : "";

  document.cookie = `${name}=${encodeURIComponent(
    JSON.stringify(value),
  )};expires=${expires};path=/;SameSite=Lax${secure}`;
};

export const deleteCookie = (name: string) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;SameSite=Lax`;
};

export const getCookieValue = (name: string): string | null => {
  const raw = getCookieRaw(name);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === "string" ? parsed : JSON.stringify(parsed);
  } catch {
    return raw;
  }
};
/* -----------------------------------------------------------
   PRODUCT ENTRY STRUCTURE
----------------------------------------------------------- */
// Cart Entry (requires qty)
export interface ProductEntry {
  mappingCode: string; // Used for Cart (mappingCode treated as variationCode)
  qty: number;
  isOutOfStock?: boolean;
}

const normalizeProductArray = (raw: unknown[]): ProductEntry[] => {
  if (!raw?.length) return [];

  return raw
    .map((item: unknown): ProductEntry => {
      // If item is a string (old format), convert to ProductEntry
      if (typeof item === "string") {
        return { mappingCode: item, qty: 1, isOutOfStock: false };
      }

      // If item is an object, extract variationCode properly
      if (typeof item === "object" && item !== null) {
        const obj = item as Record<string, unknown>;
        // Get ID from either variationCode, variationId or productId (fallback)
        let code =
          (obj.variationCode as string) || (obj.variationId as string) || (obj.productId as string) || "";

        // Ensure it's a string (not an object)
        if (typeof code === "object") {
          const codeObj = code as Record<string, unknown>;
          code = (codeObj.variationCode as string) || (codeObj.code as string) || (codeObj.id as string) || String(code);
        } else {
          code = String(code);
        }

        return {
          mappingCode: code,
          qty: (obj.qty as number) || 1,
          isOutOfStock: obj.isOutOfStock ?? false,
        };
      }

      // Fallback: convert to string
      return { mappingCode: String(item), qty: 1, isOutOfStock: false };
    })
    .filter(
      (item) => item.mappingCode && item.mappingCode !== "[object Object]",
    );
};

/* -----------------------------------------------------------
   CART HELPERS
----------------------------------------------------------- */
const CART_KEY = "cart";

export const getCartItems = (): ProductEntry[] => {
  const items = normalizeProductArray(
    safeJsonParse(getCookieRaw(CART_KEY), []),
  );
  return items;
};

export const setCartItems = (items: ProductEntry[]) => {
  setCookie(CART_KEY, items, 30);
};

export const addOrUpdateCartItem = (mappingCode: string, qty: number = 1) => {
  const items = getCartItems();
  const idx = items.findIndex((i) => i.mappingCode === mappingCode);

  if (idx >= 0) {
    items[idx].qty += qty;
  } else {
    items.push({ mappingCode, qty, isOutOfStock: false });
  }

  setCartItems(items);

  return items;
};

export const updateCartItem = (mappingCode: string, qty: number) => {
  const items = getCartItems();
  const idx = items.findIndex((i) => i.mappingCode === mappingCode);

  if (qty <= 0) {
    if (idx >= 0) items.splice(idx, 1);
  } else {
    if (idx >= 0) items[idx].qty = qty;
    else items.push({ mappingCode, qty, isOutOfStock: false });
  }

  setCartItems(items);
  return items;
};

export const removeCartItem = (mappingCode: string) => {
  const updated = getCartItems().filter((i) => i.mappingCode !== mappingCode);
  setCartItems(updated);
  return updated;
};

export const clearCart = () => {
  deleteCookie(CART_KEY);
  deleteCookie(WISHLIST_KEY);
};

/* -----------------------------------------------------------
     WISHLIST HELPERS
  ----------------------------------------------------------- */
const WISHLIST_KEY = "wishlist";

export const getWishlistItems = (): string[] => {
  const raw = safeJsonParse(getCookieRaw(WISHLIST_KEY), []);

  // Normalize to array of strings (mappingCodes)
  const items: string[] = raw
    .map((item: unknown): string => {
      if (typeof item === "string") return item;
      // Handle old format with objects
      if (typeof item === "object" && item !== null) {
        const obj = item as Record<string, unknown>;
        return (
          (obj.variationCode as string) ||
          (obj.mappingCode as string) ||
          (obj.productId as string) ||
          (obj.code as string) ||
          String(item)
        );
      }
      return String(item);
    })
    .filter((code: string) => code && code !== "[object Object]");

  return items;
};

export const addToWishlist = (mappingCode: string) => {
  const raw = safeJsonParse(getCookieRaw(WISHLIST_KEY), []);

  // Normalize to array of strings (mappingCodes)
  const items: string[] = raw
    .map((item: unknown): string => {
      if (typeof item === "string") return item;
      // Handle old format with objects
      if (typeof item === "object" && item !== null) {
        const obj = item as Record<string, unknown>;
        return (
          (obj.variationCode as string) ||
          (obj.mappingCode as string) ||
          (obj.productId as string) ||
          (obj.code as string) ||
          String(item)
        );
      }
      return String(item);
    })
    .filter((code: string) => code && code !== "[object Object]");

  // Add if not already present
  if (!items.includes(mappingCode)) {
    items.push(mappingCode);
  }

  setCookie(WISHLIST_KEY, items, 30);
  return items;
};

export const removeFromWishlist = (mappingCode: string) => {
  const raw = safeJsonParse(getCookieRaw(WISHLIST_KEY), []);

  // Normalize to array of strings
  const items: string[] = raw
    .map((item: unknown): string => {
      if (typeof item === "string") return item;
      if (typeof item === "object" && item !== null) {
        const obj = item as Record<string, unknown>;
        return (
          (obj.variationCode as string) ||
          (obj.mappingCode as string) ||
          (obj.productId as string) ||
          (obj.code as string) ||
          String(item)
        );
      }
      return String(item);
    })
    .filter((code: string) => code && code !== "[object Object]");

  // Remove the item
  const filtered = items.filter((code) => code !== mappingCode);
  setCookie(WISHLIST_KEY, filtered, 30);
  return filtered;
};

export const clearWishlist = () => {
  deleteCookie(WISHLIST_KEY);
};

/* -----------------------------------------------------------
     USER STATE SYSTEM
     - Supports random_user → guest_id → user_id promotion
     - Ensures clean, predictable user state
  ----------------------------------------------------------- */
export const resolveUserId = (): string | null => {
  return (
    getCookieValue("user_id") ||
    getCookieValue("userId") ||
    getCookieValue("userid") ||
    null
  );
};

export const resolveGuestId = (): string | null => {
  return getCookieValue("guest_id") || null;
};

/* -----------------------------------------------------------
     RANDOM USER ID
  ----------------------------------------------------------- */
export const getOrCreateRandomUserId = (): string => {
  const existingUser = resolveUserId();
  const existingGuest = resolveGuestId();

  if (existingUser) {
    deleteCookie("random_user");
    return existingUser;
  }

  if (existingGuest) {
    deleteCookie("random_user");
    return existingGuest;
  }

  let randomUser = getCookieValue("random_user");
  if (!randomUser) {
    randomUser = uuidv4();
    setCookie("random_user", randomUser, 7);
  }

  return randomUser as string;
};

/* -----------------------------------------------------------
     UNIVERSAL USER RESOLUTION (recommended)
     Returns ALWAYS:
     - user_id (if logged in)
     - guest_id (if exists)
     - random_user (fallback)
  ----------------------------------------------------------- */
export const resolveUid = (): string => {
  const userId = resolveUserId();
  if (userId) {
    // ✅ Delete all guest/random cookies when user logs in
    deleteCookie("guest_id");
    deleteCookie("random_user");
    deleteCookie("cart");
    deleteCookie("wishlist");
    return userId;
  }

  const guestId = resolveGuestId();
  if (guestId) {
    deleteCookie("random_user");
    return guestId;
  }

  return getOrCreateRandomUserId();
};

/* -----------------------------------------------------------
     USER TYPE HELPER
  ----------------------------------------------------------- */
export const getUserType = (): "user" | "guest" | "random" => {
  if (resolveUserId()) return "user";
  if (resolveGuestId()) return "guest";
  return "random";
};

/* -----------------------------------------------------------
     COOKIE-BASED STATUS CHECKERS
     For random users to check if product is in wishlist/cart
  ----------------------------------------------------------- */

/**
 * Check if a product (by mappingCode) is in the wishlist cookie
 * @param mappingCode - The product's mapping code
 * @returns true if product is in wishlist cookie, false otherwise
 */
export const isInWishlistCookie = (mappingCode: string): boolean => {
  if (!mappingCode) return false;
  const wishlistItems = getWishlistItems();
  return wishlistItems.includes(mappingCode);
};

/**
 * Check if a product (by mappingCode) is in the cart cookie
 * @param mappingCode - The product's mapping code
 * @returns true if product is in cart cookie, false otherwise
 */
export const isInCartCookie = (mappingCode: string): boolean => {
  if (!mappingCode) return false;
  const cartItems = getCartItems();
  return cartItems.some((item) => item.mappingCode === mappingCode);
};

/**
 * Get the quantity of a product in the cart cookie
 * @param mappingCode - The product's mapping code
 * @returns quantity if found, 0 otherwise
 */
export const getCartQtyFromCookie = (mappingCode: string): number => {
  if (!mappingCode) return 0;
  const cartItems = getCartItems();
  const item = cartItems.find((i) => i.mappingCode === mappingCode);

  return item?.qty || 0;
};
